import { useMemo, useState } from "react";
import {
  Image,
  type LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Svg, { Circle, G, Image as SvgImage, Path } from "react-native-svg";
import { LinearGradient } from "expo-linear-gradient";
import { ArrowRight } from "lucide-react-native";
import { ActionRowCard } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { PlanAction } from "../plan.types";

const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;
const spermIllustration =
  require("@src/apps/moon/assets/images/sperm-analysis-illustration.png") as number;

// TODO: replace with API-generated semen analysis type once the backend
// exposes uploaded/result data for sperm freezing.
export type SpermAnalysisResults = {
  volume?: string;
  concentration?: string;
  progressiveMotility?: string;
  normalMorphology?: string;
  collectedOn?: string;
  acrosome?: string;
  head?: string;
  neck?: string;
  tail?: string;
  motilityStatus?: string;
  // Advanced panel
  dnaFragmentation?: string;
  methylation?: string;
  oxidativeStress?: string;
  vitality?: string;
  aneuploidy?: string;
};

type Metric = {
  key: keyof Pick<
    SpermAnalysisResults,
    | "volume"
    | "concentration"
    | "progressiveMotility"
    | "normalMorphology"
    | "dnaFragmentation"
    | "methylation"
    | "oxidativeStress"
    | "vitality"
    | "aneuploidy"
  >;
  label: string;
  unit: string;
  description: string;
};

function useBasicMetrics(): Metric[] {
  const t = useTranslations();
  return [
    {
      key: "volume",
      label: t("homepage.sperm_analysis_skeleton.volume_label"),
      unit: t("homepage.sperm_analysis_skeleton.volume_unit"),
      description: t("homepage.sperm_analysis_skeleton.volume_description"),
    },
    {
      key: "concentration",
      label: t("homepage.sperm_analysis_skeleton.concentration_label"),
      unit: t("homepage.sperm_analysis_skeleton.concentration_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.concentration_description",
      ),
    },
    {
      key: "progressiveMotility",
      label: t("homepage.sperm_analysis_skeleton.progressive_motility_label"),
      unit: t("homepage.sperm_analysis_skeleton.progressive_motility_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.progressive_motility_description",
      ),
    },
    {
      key: "normalMorphology",
      label: t("homepage.sperm_analysis_skeleton.normal_morphology_label"),
      unit: t("homepage.sperm_analysis_skeleton.normal_morphology_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.normal_morphology_description",
      ),
    },
  ];
}

function useAdvancedMetrics(): Metric[] {
  const t = useTranslations();
  return [
    {
      key: "dnaFragmentation",
      label: t("homepage.sperm_analysis_skeleton.dna_fragmentation_label"),
      unit: t("homepage.sperm_analysis_skeleton.dna_fragmentation_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.dna_fragmentation_description",
      ),
    },
    {
      key: "methylation",
      label: t("homepage.sperm_analysis_skeleton.methylation_label"),
      unit: t("homepage.sperm_analysis_skeleton.methylation_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.methylation_description",
      ),
    },
    {
      key: "oxidativeStress",
      label: t("homepage.sperm_analysis_skeleton.oxidative_stress_label"),
      unit: t("homepage.sperm_analysis_skeleton.oxidative_stress_unit"),
      description: t(
        "homepage.sperm_analysis_skeleton.oxidative_stress_description",
      ),
    },
    {
      key: "vitality",
      label: t("homepage.sperm_analysis_skeleton.vitality_label"),
      unit: t("homepage.sperm_analysis_skeleton.vitality_unit"),
      description: t("homepage.sperm_analysis_skeleton.vitality_description"),
    },
    {
      key: "aneuploidy",
      label: t("homepage.sperm_analysis_skeleton.aneuploidy_label"),
      unit: t("homepage.sperm_analysis_skeleton.aneuploidy_unit"),
      description: t("homepage.sperm_analysis_skeleton.aneuploidy_description"),
    },
  ];
}

type Anatomy = {
  label: string;
  key: keyof Pick<
    SpermAnalysisResults,
    "acrosome" | "head" | "neck" | "tail" | "motilityStatus"
  >;
  // Anchor on the upright sperm, as a fraction of the illustration box.
  nx: number;
  ny: number;
};

function useAnatomy(): Anatomy[] {
  const t = useTranslations();
  return [
    {
      label: t("homepage.sperm_analysis_skeleton.anatomy_acrosome"),
      key: "acrosome",
      nx: 0.5,
      ny: 0.1,
    },
    {
      label: t("homepage.sperm_analysis_skeleton.anatomy_head"),
      key: "head",
      nx: 0.52,
      ny: 0.24,
    },
    {
      label: t("homepage.sperm_analysis_skeleton.anatomy_neck"),
      key: "neck",
      nx: 0.52,
      ny: 0.42,
    },
    {
      label: t("homepage.sperm_analysis_skeleton.anatomy_tail"),
      key: "tail",
      nx: 0.48,
      ny: 0.66,
    },
    {
      label: t("homepage.sperm_analysis_skeleton.anatomy_motility"),
      key: "motilityStatus",
      nx: 0.44,
      ny: 0.9,
    },
  ];
}

const DIAGRAM_HEIGHT = 244;

type Props = {
  results?: SpermAnalysisResults | null;
  onAction: (action: PlanAction) => void;
  layout?: "single" | "split";
};

function AnatomyPill({
  label,
  value,
  width,
}: {
  label: string;
  value?: string;
  width: number;
}) {
  const hasValue = Boolean(value);
  return (
    <View
      style={{
        width,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.14)",
        backgroundColor: "rgba(255,255,255,0.06)",
        paddingHorizontal: 10,
        paddingVertical: 7,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 14,
          fontWeight: "700",
          color: "rgba(255,255,255,0.6)",
        }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 17,
          fontWeight: "800",
          color: hasValue ? "#FFFFFF" : "rgba(255,255,255,0.34)",
          marginTop: 1,
        }}
      >
        {hasValue ? value : "––"}
      </Text>
    </View>
  );
}

function SpermDiagram({
  results,
  pillWidth,
}: {
  results?: SpermAnalysisResults | null;
  pillWidth: number;
}) {
  const [width, setWidth] = useState(0);
  const anatomy = useAnatomy();

  const onLayout = (e: LayoutChangeEvent) => {
    setWidth(e.nativeEvent.layout.width);
  };

  // The sperm fills the full height of the diagram so it visually spans all
  // of the anatomy callouts pointing to it. The artwork is ~square, so the
  // box is sized off the diagram height and bled slightly off the left edge.
  const spermSize = DIAGRAM_HEIGHT;
  const spermLeft = -spermSize * 0.16;
  const pillX = width > 0 ? width - pillWidth : 0;
  const rowGap = DIAGRAM_HEIGHT / anatomy.length;
  const pillHeight = 46;

  return (
    <View onLayout={onLayout} style={{ height: DIAGRAM_HEIGHT }}>
      <View
        style={{
          position: "absolute",
          left: spermLeft,
          top: 0,
          width: spermSize,
          height: spermSize,
        }}
      >
        {/* Faithful reproduction of the supplied Sperm.svg: the bitmap is
            drawn into a rect that the SVG itself rotates, which is what makes
            it read upright. No extra rotation is added here. */}
        <Svg width={spermSize} height={spermSize} viewBox="0 0 199 200">
          <G rotation={-52.6725} originX={0} originY={115.82}>
            <SvgImage
              href={spermIllustration}
              x={0}
              y={115.82}
              width={145.653}
              height={138.605}
              preserveAspectRatio="none"
            />
          </G>
          <Circle
            cx={91.9004}
            cy={21.5869}
            r={21.5}
            fill="#FFFFFF"
            fillOpacity={0.1}
          />
        </Svg>
      </View>

      {width > 0 ? (
        <Svg
          width={width}
          height={DIAGRAM_HEIGHT}
          style={StyleSheet.absoluteFill}
        >
          {anatomy.map((a, i) => {
            const px = spermLeft + a.nx * spermSize;
            const py = a.ny * spermSize;
            const ly = rowGap * (i + 0.5);
            const elbowX = pillX - 14;
            return (
              <Path
                key={a.label}
                d={`M ${px} ${py} L ${elbowX} ${py} L ${pillX - 2} ${ly}`}
                stroke="rgba(255,255,255,0.26)"
                strokeWidth={1}
                fill="none"
              />
            );
          })}
          {anatomy.map((a) => (
            <Circle
              key={`${a.label}-dot`}
              cx={spermLeft + a.nx * spermSize}
              cy={a.ny * spermSize}
              r={2.5}
              fill="rgba(255,255,255,0.6)"
            />
          ))}
        </Svg>
      ) : null}

      {anatomy.map((a, i) => (
        <View
          key={a.label}
          style={{
            position: "absolute",
            left: pillX,
            top: rowGap * (i + 0.5) - pillHeight / 2,
          }}
        >
          <AnatomyPill
            label={a.label}
            value={results?.[a.key]}
            width={pillWidth}
          />
        </View>
      ))}
    </View>
  );
}

function MetricCell({
  metric,
  value,
  fullWidth,
  compact,
}: {
  metric: Metric;
  value?: string;
  fullWidth?: boolean;
  compact?: boolean;
}) {
  const hasValue = Boolean(value);
  return (
    <View
      style={{
        ...(fullWidth
          ? { alignSelf: "stretch" }
          : { flexBasis: "48%", flexGrow: 1 }),
        borderRadius: compact ? 12 : 14,
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.1)",
        backgroundColor: "rgba(255,255,255,0.05)",
        paddingHorizontal: compact ? 12 : 14,
        paddingVertical: compact ? 9 : 12,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 4 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: compact ? 17 : 20,
            lineHeight: compact ? 21 : 24,
            fontWeight: "800",
            color: hasValue ? "#FFFFFF" : "rgba(255,255,255,0.32)",
          }}
        >
          {hasValue ? value : "––"}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: compact ? 10 : 11,
            lineHeight: compact ? 14 : 15,
            fontWeight: "700",
            color: hasValue
              ? "rgba(255,255,255,0.55)"
              : "rgba(255,255,255,0.25)",
          }}
        >
          {metric.unit}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: compact ? 11 : 13,
          lineHeight: compact ? 14 : 17,
          fontWeight: "700",
          color: "#FFFFFF",
          marginTop: compact ? 2 : 4,
        }}
      >
        {metric.label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: compact ? 10 : 12,
          lineHeight: compact ? 13 : 17,
          fontWeight: "500",
          color: "rgba(255,255,255,0.5)",
          marginTop: 3,
        }}
      >
        {metric.description}
      </Text>
    </View>
  );
}

type TabKey = "basic" | "advanced";

function TabBar({
  tab,
  onChange,
}: {
  tab: TabKey;
  onChange: (next: TabKey) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TabBar");
  const tabs: { key: TabKey; label: string }[] = useMemo(
    () => [
      { key: "basic", label: t("homepage.sperm_analysis_skeleton.tab_basic") },
      {
        key: "advanced",
        label: t("homepage.sperm_analysis_skeleton.tab_advanced"),
      },
    ],
    [t],
  );
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 4,
        marginTop: 14,
        padding: 4,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.1)",
        backgroundColor: "rgba(255,255,255,0.05)",
      }}
    >
      {tabs.map((item) => {
        const active = item.key === tab;
        return (
          <Pressable
            key={item.key}
            onPress={() => {
              capturePress(
                pressLabel(item.label),
                `homepage.sperm_analysis_skeleton.tab_${item.key}`,
              );
              onChange(item.key);
            }}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 9,
              alignItems: "center",
              backgroundColor: active
                ? "rgba(255,255,255,0.14)"
                : "transparent",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 17,
                fontWeight: "700",
                color: active ? "#FFFFFF" : "rgba(255,255,255,0.55)",
              }}
            >
              {item.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export function SpermAnalysisSkeletonCard({
  results,
  onAction,
  layout = "single",
}: Props) {
  const t = useTranslations();
  const isSplit = layout === "split";
  const [tab, setTab] = useState<TabKey>("basic");
  const basicMetrics = useBasicMetrics();
  const advancedMetrics = useAdvancedMetrics();
  const hasResults = Boolean(
    results &&
    (results.volume ||
      results.concentration ||
      results.progressiveMotility ||
      results.normalMorphology),
  );

  const cta: PlanAction =
    tab === "advanced"
      ? {
          label: t("homepage.sperm_analysis_skeleton.cta_explore_advanced"),
          screen: "AtHomeFertilityTesting",
          params: { focus: "advanced-sperm" },
        }
      : hasResults
        ? {
            label: t("homepage.shared.upload_semen_analysis_results"),
            signal: "open-document-upload",
            params: {
              initialCategory: "medical_records",
            },
          }
        : {
            label: t("homepage.sperm_analysis_skeleton.cta_get_analysis"),
            screen: "AtHomeFertilityTesting",
            params: { focus: "semen-analysis" },
          };

  // Empty semen analysis is an upload/get-results action, not a card full of
  // invented metric slots. Keep the populated diagram for recorded values,
  // but use the same canonical action-row language as other empty test states.
  if (!hasResults) {
    const title = t("homepage.shared.semen_analysis");
    const subtitle = t(
      "homepage.review_concept.inline.sperm_wait_data_not_recorded_detail",
    );
    return (
      <ActionRowCard
        leading={
          <Image
            source={spermIllustration}
            resizeMode="contain"
            style={{ width: 32, height: 32 }}
          />
        }
        title={title}
        subtitle={subtitle}
        onPress={() => onAction(cta)}
        accessibilityLabel={`${title}. ${subtitle}`}
      />
    );
  }

  return (
    <View
      style={{
        marginTop: 24,
        borderRadius: 24,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.18)",
      }}
    >
      <Image
        source={silkBrown}
        style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
        resizeMode="cover"
      />
      <View
        style={{
          backgroundColor: "rgba(255,255,255,0.12)",
          paddingHorizontal: 20,
          paddingTop: 20,
          paddingBottom: 20,
          ...(Platform.OS === "web"
            ? ({
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
              } as Record<string, string>)
            : {}),
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 14,
            fontWeight: "800",
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          {t("homepage.shared.semen_analysis")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 17,
            lineHeight: 22,
            fontWeight: "700",
            color: "#FFFFFF",
            marginTop: 6,
          }}
        >
          {hasResults
            ? t("homepage.sperm_analysis_skeleton.your_sperm_analysis")
            : t("homepage.sperm_analysis_skeleton.what_is_going_on")}
        </Text>

        <TabBar tab={tab} onChange={setTab} />

        {tab === "basic" ? (
          isSplit ? (
            <View
              style={{
                flexDirection: "row",
                gap: 14,
                marginTop: 16,
              }}
            >
              <View style={{ flex: 2 }}>
                <SpermDiagram results={results} pillWidth={104} />
              </View>
              <View
                style={{ flex: 1, gap: 8, justifyContent: "space-between" }}
              >
                {basicMetrics.map((metric) => (
                  <MetricCell
                    key={metric.key}
                    metric={metric}
                    value={results?.[metric.key]}
                    fullWidth
                    compact
                  />
                ))}
              </View>
            </View>
          ) : (
            <>
              <View style={{ marginTop: 16 }}>
                <SpermDiagram results={results} pillWidth={116} />
              </View>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {basicMetrics.map((metric) => (
                  <MetricCell
                    key={metric.key}
                    metric={metric}
                    value={results?.[metric.key]}
                  />
                ))}
              </View>
            </>
          )
        ) : (
          <View style={{ marginTop: 16, gap: 10 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                lineHeight: 19,
                fontWeight: "500",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              {t("homepage.sperm_analysis_skeleton.advanced_intro")}
            </Text>
            {advancedMetrics.map((metric) => (
              <MetricCell
                key={metric.key}
                metric={metric}
                value={results?.[metric.key]}
                fullWidth
              />
            ))}
          </View>
        )}

        <Pressable
          onPress={() => onAction(cta)}
          style={({ pressed }) => ({
            marginTop: 18,
            opacity: pressed ? 0.85 : 1,
          })}
        >
          <LinearGradient
            colors={["rgba(255, 255, 255, 0.13)", "rgba(255, 255, 255, 0.06)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={{
              paddingVertical: 14,
              paddingHorizontal: 20,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.11)",
              borderRadius: 14,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "700",
                color: "#FFFFFF",
                letterSpacing: 0.2,
              }}
            >
              {cta.label}
            </Text>
            <ArrowRight size={16} color="#FFFFFF" strokeWidth={2} />
          </LinearGradient>
        </Pressable>
      </View>
    </View>
  );
}
